// IMPORTANT: BaseToolbar is only for internal use.
// Consumer should only use Toolbar at all times
import { forwardRef, Fragment, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Overflow } from '@sas/react-svg';
import cx from 'classnames';
import { getAlwaysOverflowItems, getAvailableWidth, getMinimalStateItems, getNextRestoreIndex, getOveflowedItems, getResponsiveIndices, getResponsiveSets, ToolbarSpacer, useOverflowConfigs } from '.';
import { BrowserName, getBrowserName } from '../../../browser-name';
import { ContentSearchField } from '../../../content/ContentSearchField';
import { useRefArray } from '../../../hooks/useRefArray';
import { useResizeObserverRef } from '../../../hooks/useResizeObserver';
import { createUseTextLocalizer } from '../../../locale/createUseTextLocalizer';
import { renderInlineCssVars, useDesignTokens } from '../../../theme';
import { setRef } from '../../../util/ref-util';
import { postfixTestId } from '../../../util/testId';
import { Breadcrumb } from '../../Breadcrumb';
import { ButtonVariant } from '../../Button';
import { ComboBox } from '../../ComboBox';
import { CompositeInput } from '../../CompositeInput';
import { DatePicker, DatePickerWithLabels } from '../../DatePicker';
import { useDensity } from '../../Density';
import { Dialog } from '../../Dialog';
import { FileUploader } from '../../FileUploader';
import { Input } from '../../Input';
import { MessageStrip } from '../../MessageStrip';
import { SearchField } from '../../SearchField';
import { Switch } from '../../Switch';
import { Tokenizer } from '../../Tokenizer';
import { withLabels } from '../../withLabels';
import { commonsWithLabelsProps, InnerToolbar, renderInnerToolbarItem } from './InnerToolbar/InnerToolbar';
import { FocusableItemTypes, InnerToolbarItemTypes } from './InnerToolbar/types';
import bundle from '../NovaComponents-Toolbar-gui-icu.properties';
import cn from './BaseToolbar.css.json';
import './BaseToolbar.css';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useTextLocalizer = createUseTextLocalizer(bundle);

/**
 * @internal
 */
export const BaseToolbar = /*#__PURE__*/forwardRef(function BaseToolbar(props, forwardRef) {
  const dt = useDesignTokens();
  const refToolbar = useRef(null);
  const {
    refs: itemContainers,
    createSetRef: createItemRef
  } = useRefArray();
  const [dialogContentItemIndex, setDialogContentItemIndex] = useState();
  const [itemMaxWidths, setItemMaxWidths] = useState([]);
  const initialRef = useRef(true);
  const {
    density
  } = useDensity();
  const localize = useTextLocalizer();
  const items = useMemo(() => {
    initialRef.current = true;
    return props.items.reduce((prevItems, item) =>
    // eslint-disable-next-line @sas/i18n/jsx-to-localize
    item.type === 'Tokenizer' && !item.hideSeparators ?
    // Add separator before and after Tokenizer
    [...prevItems, {
      type: 'ToolbarSeparator',
      testId: postfixTestId(item.testId, 'separator-before')
    }, item, {
      type: 'ToolbarSeparator',
      testId: postfixTestId(item.testId, 'separator-after')
    }] : [...prevItems, item], []);
  }, [props.items]);
  const overflowConfigs = useOverflowConfigs(items, props.responsiveSetsConfig);
  const responsiveSets = useMemo(() => getResponsiveSets(overflowConfigs), [overflowConfigs]);
  const overflowPriorities = useMemo(() => overflowConfigs.map(({
    overflowPriority
  }) => overflowPriority), [overflowConfigs]);
  const [{
    overflowedItems,
    minimalStateItems
  }, setOverflowedState] = useState({
    overflowedItems: [],
    minimalStateItems: []
  });
  const overflowed = useMemo(() => [...overflowedItems, ...getAlwaysOverflowItems(overflowPriorities)], [overflowPriorities, overflowedItems]);
  const overflowedIndices = overflowed.map(({
    index
  }) => index);
  // Sort indices in ascending order to keep the items in order in menuItem
  overflowedIndices.sort((index1, index2) => index1 - index2);
  const hasItemToRestore = getNextRestoreIndex(overflowPriorities, overflowedIndices) >= 0;
  const {
    onOverflowCalcChange
  } = props;
  const [overflowCalcComplete, setOverflowCalcComplete] = useState(false);
  const responsivenessHandler = useCallback(extraSpacerRefs => {
    setOverflowCalcComplete(false);
    setOverflowedState(prevOverflowedState => {
      const {
        overflowedItems: prevOverflowedItems,
        minimalStateItems: prevMinimalStateItems
      } = prevOverflowedState;
      let overflowedItems = prevOverflowedItems;
      let minimalStateItems = prevMinimalStateItems;
      const availableWidth = getAvailableWidth(items, refToolbar.current, itemContainers, extraSpacerRefs, overflowPriorities.length === 1 && overflowPriorities[0] !== 'always');
      const hasItemToRespond = getResponsiveIndices(items, overflowed, prevMinimalStateItems).length > 0;
      if (availableWidth < 0 && hasItemToRespond ||
      // Only restore the minimal state items when there is nothing to restore in the overflowed menu button
      availableWidth > 0 && !hasItemToRestore) {
        minimalStateItems = getMinimalStateItems({
          items,
          overflowed,
          prevMinimalStateItems,
          availableWidth,
          itemContainers,
          responsiveSets
        });
      } else {
        overflowedItems = getOveflowedItems(overflowPriorities, prevOverflowedItems, availableWidth, itemContainers, responsiveSets);
      }
      setOverflowCalcComplete(true);
      return minimalStateItems === prevMinimalStateItems && overflowedItems === prevOverflowedItems ? prevOverflowedState // Return the same object if nothing changes to avoid infinite loops
      : {
        minimalStateItems,
        overflowedItems
      };
    });
  }, [items, itemContainers, overflowed, hasItemToRestore, responsiveSets, overflowPriorities]);
  const prevClientWidthRef = useRef();
  const [setResizeObserverRef] = useResizeObserverRef(() => {
    // This callback function is called when Toolbar re-renders with the same width.
    // So return early to skip the unnecessary responsiveness calculation for performance reasons
    if (prevClientWidthRef.current === refToolbar.current?.clientWidth) return;
    prevClientWidthRef.current = refToolbar.current?.clientWidth;
    responsivenessHandler();
  });
  useImperativeHandle(props.toolbarRef, () => ({
    responsivenessHandler
  }));

  // This useEffect must be placed before the useEffect that handles overflowing so that
  // all the components are rendered, thus have computedStyles
  useEffect(() => {
    if (!initialRef.current || itemContainers.length === 0) return;
    setItemMaxWidths(itemContainers.map(itemContainer => {
      if (!itemContainer?.children?.[0]) return 'none';
      return getComputedStyle(itemContainer.children[0]).maxWidth;
    }));
    initialRef.current = false;
  }, [itemContainers, itemMaxWidths, items]);
  {
    const itemTypes = items.map(({
      type
    }) => type);
    const [prevOverflowPriorities, setPrevOverflowPriorities] = useState('');
    const [prevItemTypes, setPrevItemTypes] = useState('');
    // Reset overflowed state if overflow priorites change
    if (prevOverflowPriorities && prevOverflowPriorities !== overflowPriorities.toString()) {
      setPrevOverflowPriorities(overflowPriorities.toString());
      setOverflowedState({
        overflowedItems: [],
        minimalStateItems: []
      });
    }
    // Reset overflowed state and max-width if item types change
    // Note: it is assumed that component's max-width doesn't change if its type doesn't change,
    // but technically it isn't true
    if (prevItemTypes && prevItemTypes !== itemTypes.toString()) {
      setPrevItemTypes(itemTypes.toString());
      setOverflowedState({
        overflowedItems: [],
        minimalStateItems: []
      });
      setItemMaxWidths([]);
    }
  }
  // Exclude items of 'disappear' overflowPriority from the menu item
  const overflowedMenuItemIndices = overflowedIndices.filter(index => overflowConfigs[index]?.overflowPriority !== 'disappear');
  const sections = getOverflowMenuButtonSections(overflowedMenuItemIndices, overflowConfigs, items);
  const overflowMenuButton = overflowedMenuItemIndices.length === 0 ? undefined : {
    type: 'BaseMenuButton',
    icon: {
      icon: Overflow
    },
    variant: ButtonVariant.Icon,
    hideDownArrow: true,
    testId: postfixTestId(props.testId, 'overflowMenuButton'),
    tooltip: props.overflowMenuButtonTooltip ?? localize('overflow.menubutton.tooltip.label'),
    ariaLabel: props.overflowMenuButtonAriaLabel ?? localize('overflow.menubutton.aria'),
    ...props.overflowMenuButtonProps,
    sections: sections.map(({
      items
    }) => {
      return {
        items
      };
    }),
    onItemSelected: (...args) => {
      const [indices] = args;
      if (indices.length < 1) return;
      const [sectionIndex, itemIndex] = indices[0];
      const onSelectedHandlers = sections[sectionIndex].onSelectedHandlers;
      const customDialogProps = onSelectedHandlers[
      // Use Math.min here because when one component overflows into
      // multiple menu items, they share one onSelectedHandler
      Math.min(itemIndex, onSelectedHandlers.length - 1)]?.(...args);
      if (customDialogProps) {
        // When onSelectedHandlers returns customDialogProps, dialog is opened
        // Stores index for future dialog content updates
        setDialogContentItemIndex(customDialogProps.index);
      }
    },
    onItemClosed: indices => {
      const [sectionIndex, itemIndex] = indices[0];
      const onItemClosedHandlers = sections[sectionIndex].onItemClosedHandlers[itemIndex];
      onItemClosedHandlers?.(indices);
    },
    menuAlign: {
      horizontalAlign: 'end'
    },
    containerStyle: props.overflowMenuButtonProps?.containerStyle
  };

  // Explicitly re-trigger responsiveness if anything changes
  useEffect(() => {
    responsivenessHandler();
  }, [responsivenessHandler, minimalStateItems]);

  // in useEffect to allow DOM manipulation
  useEffect(() => {
    if (onOverflowCalcChange) {
      onOverflowCalcChange(overflowCalcComplete);
    }
  }, [overflowCalcComplete, onOverflowCalcChange]);
  const nonOverflowedIndices = getNonOverflowedIndices(items, overflowedIndices, props.disableAutoGrouping, overflowMenuButton !== undefined, props.groupThreshold, props.disableDropLeadingSeparator);
  const itemsToRender = items.map(({
    ...item
  }, i) => {
    // Without display: 'flex', Safari and Firefox will strech the conatianer of
    // certain components such as Input to its full height, resulting them not being center aligned
    if (item.type !== 'ToolbarSpacer') {
      item.containerStyle = {
        display: 'flex',
        ...item.containerStyle
      };
    }
    if (item.type === 'Select' || item.type === 'SearchField' || item.type === 'ContentSearchField' || item.type === 'Input' || item.type === 'ComboBox' || item.type === 'CompositeInput' || item.type === 'FileUploader' || item.type === 'Label' || item.type === 'Checkbox' || item.type === 'Heading' || item.type === 'Breadcrumb' || item.type === 'Tokenizer') {
      item.containerStyle = {
        minWidth: item.style?.minWidth,
        ...item.containerStyle
      };
      if (!item.containerStyle.minWidth) {
        if (item.type === 'Label' || item.type === 'Heading') {
          item.containerStyle.minWidth = '2rem';
        } else if (item.type === 'Checkbox') {
          // Only the Checkbox with label should be shrinkable
          item.containerStyle.minWidth = item.label ? '3rem' : undefined;
        } else if (item.type === 'CompositeInput') {
          item.containerStyle.minWidth = '12rem';
        } else if (item.type === 'FileUploader') {
          item.containerStyle.minWidth = item.buttonOnly ? undefined : '12rem';
        } else if (item.type === 'Tokenizer') {
          // Only set min-width when there are one or more tokens
          if (item.tokens.length > 1) {
            item.containerStyle.minWidth = '8rem';
          } else if (item.tokens.length === 1) {
            // Scroll buttons won't show up if there is only one token, so use a smaller min-width
            item.containerStyle.minWidth = '4rem';
          }
        } else if (item.type === 'Breadcrumb') {
          const length = item.values?.length ?? 0;
          // Only set min-width when there are one or more breadcrumbs
          if (length > 1) {
            item.containerStyle.minWidth = '12rem';
          } else if (length === 1) {
            // Scroll buttons won't show up if there is only one breadcrumb, so use a smaller min-width
            item.containerStyle.minWidth = '3rem';
          }
        } else {
          item.containerStyle.minWidth = '6rem';
        }
      }

      // Override the maxWidth so that the component doesn't grow outside of container
      // Safari 'max-width: min(100%)' behaves differently than 'max-width: 100%'.
      // It seems like when inside of the min() function Safari rounds down to the
      // nearest pixel causing truncation.
      const isSafari = getBrowserName() === BrowserName.Safari;
      const defaultMaxWidth = isSafari ? '100% + 1px' : '100%';
      if (itemMaxWidths[i]) {
        item.style = {
          boxSizing: 'border-box',
          ...item.style,
          maxWidth:
          // In FireFox, 'max-width: 100%' behaves differently than 'max-width: min(100%, 100%)',
          // so avoid using `min` if the item max-width is also '100%'
          itemMaxWidths[i] === 'none' || itemMaxWidths[i] === '100%' ? '100%' : `min(${defaultMaxWidth}, ${itemMaxWidths[i]})`
        };

        // Container should have the same max width as its children
        if (item.containerStyle.maxWidth === undefined) {
          item.containerStyle.maxWidth = itemMaxWidths[i];
        }
      }
    } else if (item.type === 'SelectWithLabels' || item.type === 'SearchFieldWithLabels' || item.type === 'DatePickerWithLabels' || item.type === 'InputWithLabels' || item.type === 'ComboBoxWithLabels' || item.type === 'CompositeInputWithLabels' || item.type === 'FileUploaderWithLabels') {
      item.fieldLabelMinWidth = item.fieldLabelMinWidth ?? '2rem';
      item.fieldSuffixMinWidth = item.fieldSuffixMinWidth ?? '2rem';
      if (item.type === 'CompositeInputWithLabels') {
        item.fieldMinWidth = item.fieldMinWidth ?? '12rem';
      } else if (item.type === 'FileUploaderWithLabels') {
        item.fieldMinWidth = item.fieldMinWidth ?? '12rem';
      } else {
        item.fieldMinWidth = item.fieldMinWidth ?? '6rem';
      }
      item.containerStyle = {
        minWidth: 'suffix' in item && item.suffix ? `calc(${item.fieldLabelMinWidth} + ${item.fieldMinWidth} + ${item.fieldSuffixMinWidth})` : `calc(${item.fieldLabelMinWidth} + ${item.fieldMinWidth})`
      };
    }
    if (minimalStateItems.some(({
      index
    }) => index === i)) {
      if (item.type === 'PredefinedButton') {
        item.iconOnly = true;
      } else if (item.type === 'Button') {
        item.tooltip = item.tooltip ?? item.text;
        item.text = ''; // Drop the text
      } else if (item.type === 'ToggleButton') {
        item.tooltip = item.tooltip ?? (item.textToDrop ? item.textToDrop.trim() : item.text);
        item.ariaLabel = item.ariaLabel ?? item.text;
        item.text = item.textToDrop ? item.text?.replace(item.textToDrop, '') : '';
      } else if (item.type === 'FileUploader') {
        item.menuItemText = item.menuItemText ?? item.buttonText;
        item.buttonText = ''; // Drop the text
      }
    }

    return item;
  });
  const customDialogProps = dialogContentItemIndex === undefined ? undefined : overflowConfigs[dialogContentItemIndex]?.onSelected?.([]);
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsxs("div", {
      ref: element => {
        setResizeObserverRef(element);
        refToolbar.current = element;
        setRef(forwardRef, element);
      },
      role: props.role ? props.role : 'group',
      "aria-labelledby": props.ariaLabelledby,
      className: cx(cn.toolbar, props.className),
      style: {
        ...renderInlineCssVars({
          xs: density === 'high' ? dt.tokens['space-base-xs'] : dt.tokens['space-base-s']
        }, 'toolbar-spacing'),
        ...props.style
      },
      "data-testid": props.testId,
      children: [nonOverflowedIndices.map((itemIndices, i) => {
        // When the first item is ToolbarSpacer, suppress the second item's margin because
        // the second item is looking like the first item and should not have margin
        // Note: when the first few items are all ToolbarSpacer, which is not a valid use case,
        // so not going to cover it here
        const suppressMargin = i === 1 && typeof nonOverflowedIndices[0] === 'number' && itemsToRender[nonOverflowedIndices[0]].type === 'ToolbarSpacer';
        if (typeof itemIndices === 'object') {
          const innerToolbarItems = itemIndices.map(itemIndex => {
            // item can be casted to InnerToolbarItem, so try to group it in InnerToolbar
            // remove the ComponentOverflowConfig properties from item before pushing it into innerToolbar
            const {
              overflowPriority,
              menuItemText,
              responsiveSetId,
              ...innerToolbarItem
            } = itemsToRender[itemIndex];
            return innerToolbarItem;
          });
          if (i === nonOverflowedIndices.length - 1 && overflowMenuButton) {
            innerToolbarItems.push(overflowMenuButton);
          }
          innerToolbarItems[0].containerStyle = {
            marginInlineStart: i > 0 && !suppressMargin ? dt.tokens['space-inline-xs'] : undefined,
            ...innerToolbarItems[0].containerStyle
          };
          return /*#__PURE__*/_jsx(InnerToolbar, {
            items: innerToolbarItems,
            createSetRef: i => i < itemIndices.length ? createItemRef(itemIndices[i]) : createItemRef(itemsToRender.length)
          }, itemIndices[0]);
        } else {
          const item = itemsToRender[itemIndices];
          const containerStyle = item.type === 'ToolbarSpacer' ? undefined : item.containerStyle;
          const containerKey = item.type === 'ToolbarSpacer' ? undefined : item.containerKey;
          return /*#__PURE__*/_jsx("div", {
            className: cx(item.type === 'ToolbarSpacer' && cn.spacerContainer, item.type === 'ToolbarSeparator' && cn.separatorContainer),
            style: {
              marginInlineStart: suppressMargin ? 0 : undefined,
              ...containerStyle
            },
            ref: createItemRef(itemIndices),
            children: renderItem(item)
          }, containerKey ?? itemIndices);
        }
      }), (nonOverflowedIndices.length === 0 || typeof nonOverflowedIndices[nonOverflowedIndices.length - 1] === 'number') && overflowMenuButton ? /*#__PURE__*/_jsx("div", {
        ref: createItemRef(itemsToRender.length),
        style: overflowMenuButton.containerStyle,
        children: renderItem(overflowMenuButton)
      }, "overflowMenuButton") : null]
    }), /*#__PURE__*/_jsx(Dialog, {
      title: customDialogProps ? customDialogProps.title : '',
      isOpen: dialogContentItemIndex !== undefined,
      dismissAction: {
        text: customDialogProps && customDialogProps.dismissText ? customDialogProps.dismissText : localize('dialog.dismissButton.default.title'),
        onPress: () => {
          setDialogContentItemIndex(undefined);
        }
      },
      children: dialogContentItemIndex !== undefined && customDialogProps && items[dialogContentItemIndex] ? customDialogProps.renderContent(items[dialogContentItemIndex]) : null
    })]
  });
});
const getNonOverflowedIndices = (items, overflowedIndices, disableAutoGrouping = false, hasOverflowMenuButton, groupThreshold = 3, disableDropLeadingSeparator) => {
  const nonOverflowedIndices = [];
  // This code block figures out what should be put into nonOverflowedIndices array
  // 3 or more consecutive focusable inner toolbar items are automatically grouped together
  // Note that if it is the last group, then 2 or more consecutive focusable inner toolbar items
  // are grouped together if there is overflowMenuItem
  let innerToolbarIndices = [];
  const tryToGroup = isLastGroup => {
    // ToolbarSeparator does not count as a InnerToolbarItem
    const itemsCount = innerToolbarIndices.reduce((prevCount, itemIndex) => {
      const itemType = items[itemIndex].type;
      if (itemType === 'UndoRedo') {
        return prevCount + 2;
      }
      return FocusableItemTypes.includes(itemType) ? prevCount + 1 : prevCount;
    }, 0);
    if (isLastGroup && hasOverflowMenuButton) {
      groupThreshold -= 1;
    }
    if (itemsCount >= groupThreshold && !disableAutoGrouping) {
      // When the number of consecutive InnerToolbarItems reaches the groupThreshold, group them in InnerToolbar
      nonOverflowedIndices.push(innerToolbarIndices);
    } else {
      nonOverflowedIndices.push(...innerToolbarIndices);
    }
    innerToolbarIndices = [];
  };
  const tempNonOverflowedIndices = items.reduce((prevIndices, _, index) => {
    // Drop the separator if it is adjacent to a Toolbar spacer, another Toolbar separator or
    // if it would appear as the first or last item on the toolbar.
    // Note: if disableDropLeadingSeparator is true, it will not drop a separator that is the first item
    const currIndices = [...prevIndices];
    const prevItemType = currIndices.length > 0 ? items[currIndices[currIndices.length - 1]].type : null;
    if (!overflowedIndices.includes(index)) {
      const type = items[index].type;
      if (type === 'ToolbarSeparator') {
        if (prevItemType !== null || disableDropLeadingSeparator) {
          // Only add the separator if it is not the first item (unless disableDropLeadingSeparator is true)
          // and its previous item is neither separator nor spacer
          if (prevItemType !== 'ToolbarSeparator' && prevItemType !== 'ToolbarSpacer') {
            currIndices.push(index);
          }
        }
      } else {
        if (type === 'ToolbarSpacer' && prevItemType === 'ToolbarSeparator') {
          // Remove the spacer's previous item if it is separator
          currIndices.splice(currIndices.length - 1);
        }
        currIndices.push(index);
      }
    }
    if (index === items.length - 1) {
      const lastItemType = currIndices.length > 0 ? items[currIndices[currIndices.length - 1]].type : null;
      if (lastItemType === 'ToolbarSeparator') {
        // Remove the last item if it is separator
        currIndices.splice(currIndices.length - 1);
      }
    }
    return currIndices;
  }, []);
  for (const nonOverflowedIndex of tempNonOverflowedIndices) {
    const item = items[nonOverflowedIndex];
    const type = item.type;
    if (InnerToolbarItemTypes.includes(type) ||
    // Button-only FileUploader is also considered as an inner toolbar item
    type === 'FileUploader' && item.buttonOnly) {
      if (type === 'ToolbarSeparator' && innerToolbarIndices.length === 0) {
        // Does not group separator into InnerToolbar when it is the first item for spacing purpose
        nonOverflowedIndices.push(nonOverflowedIndex);
      } else {
        innerToolbarIndices.push(nonOverflowedIndex);
      }
    } else {
      tryToGroup(false);
      nonOverflowedIndices.push(nonOverflowedIndex);
    }
  }

  // Push the rest items to nonOverflowedIndices
  tryToGroup(true);
  return nonOverflowedIndices;
};
export const InputWithLabels = withLabels(Input);
export const ComboBoxWithLabels = withLabels(ComboBox);
export const SearchFieldWithLabels = withLabels(SearchField);
export const SwitchWithLabels = withLabels(Switch);
export const CompositeInputWithLabels = withLabels(CompositeInput);
export const FileUploaderWithLabels = withLabels(FileUploader);
export const renderItem = item => {
  switch (item.type) {
    case 'DatePicker':
      {
        const {
          datePickerProps
        } = item;
        return /*#__PURE__*/_jsx(DatePicker, {
          ...datePickerProps
        });
      }
    case 'DatePickerWithLabels':
      {
        const {
          type,
          containerStyle,
          containerKey,
          datePickerProps,
          ...rest
        } = item;
        const {
          className: inputClassName,
          style: inputStyle,
          ...restDatePickerProps
        } = datePickerProps;
        return /*#__PURE__*/_jsx(DatePickerWithLabels, {
          ...rest,
          ...restDatePickerProps,
          ...commonsWithLabelsProps,
          inputClassName: inputClassName,
          inputStyle: inputStyle
        });
      }
    case 'CompositeInput':
      {
        const {
          type,
          containerStyle,
          containerKey,
          ...rest
        } = item;
        return /*#__PURE__*/_jsx(CompositeInput, {
          ...rest
        });
      }
    case 'CompositeInputWithLabels':
      {
        const {
          type,
          containerStyle,
          containerKey,
          ...rest
        } = item;
        return /*#__PURE__*/_jsx(CompositeInputWithLabels, {
          ...rest,
          ...commonsWithLabelsProps
        });
      }
    case 'FileUploader':
      {
        const {
          type,
          overflowPriority,
          menuItemText,
          containerStyle,
          containerKey,
          ...rest
        } = item;
        return /*#__PURE__*/_jsx(FileUploader, {
          ...rest
        });
      }
    case 'FileUploaderWithLabels':
      {
        const {
          type,
          overflowPriority,
          menuItemText,
          containerStyle,
          containerKey,
          ...rest
        } = item;
        return /*#__PURE__*/_jsx(FileUploaderWithLabels, {
          ...rest,
          ...commonsWithLabelsProps
        });
      }
    case 'Breadcrumb':
      {
        const {
          type,
          containerStyle,
          containerKey,
          ...rest
        } = item;
        return /*#__PURE__*/_jsx(Breadcrumb, {
          ...rest,
          orientation: item.orientation ?? 'HORIZONTAL'
        });
      }
    case 'SearchField':
      {
        const {
          type,
          overflowPriority,
          menuItemText,
          containerStyle,
          containerKey,
          ...rest
        } = item;
        return /*#__PURE__*/_jsx(SearchField, {
          ...rest
        });
      }
    case 'SearchFieldWithLabels':
      {
        const {
          type,
          overflowPriority,
          menuItemText,
          containerStyle,
          containerKey,
          ...rest
        } = item;
        return /*#__PURE__*/_jsx(SearchFieldWithLabels, {
          ...rest,
          ...commonsWithLabelsProps,
          labelingAttr: "aria-label"
        }); // SearchField only supports ariaLabel now
      }

    case 'ContentSearchField':
      {
        const {
          type,
          containerStyle,
          containerKey,
          ...rest
        } = item;
        return /*#__PURE__*/_jsx(ContentSearchField, {
          ...rest
        });
      }
    case 'SwitchWithLabels':
      {
        const {
          type,
          overflowPriority,
          menuItemText,
          containerStyle,
          containerKey,
          ...rest
        } = item;
        return /*#__PURE__*/_jsx(SwitchWithLabels, {
          ...rest,
          ...commonsWithLabelsProps
        });
      }
    case 'Switch':
      {
        const {
          type,
          overflowPriority,
          menuItemText,
          containerStyle,
          containerKey,
          ...rest
        } = item;
        return /*#__PURE__*/_jsx(Switch, {
          ...rest
        });
      }
    case 'Input':
      {
        const {
          type,
          overflowPriority,
          menuItemText,
          containerStyle,
          containerKey,
          inputType,
          ...rest
        } = item;
        return /*#__PURE__*/_jsx(Input, {
          ...rest,
          type: inputType
        });
      }
    case 'InputWithLabels':
      {
        const {
          type,
          overflowPriority,
          menuItemText,
          containerStyle,
          containerKey,
          inputType,
          ...rest
        } = item;
        return /*#__PURE__*/_jsx(InputWithLabels, {
          ...rest,
          ...commonsWithLabelsProps,
          type: inputType
        });
      }
    case 'ComboBox':
      {
        const {
          type,
          overflowPriority,
          menuItemText,
          containerStyle,
          containerKey,
          comboBoxType,
          ...rest
        } = item;
        return /*#__PURE__*/_jsx(ComboBox, {
          ...rest,
          type: comboBoxType
        });
      }
    case 'ComboBoxWithLabels':
      {
        const {
          type,
          overflowPriority,
          menuItemText,
          containerStyle,
          containerKey,
          comboBoxType,
          ...rest
        } = item;
        return /*#__PURE__*/_jsx(ComboBoxWithLabels, {
          ...rest,
          ...commonsWithLabelsProps,
          type: comboBoxType
        });
      }
    case 'Tokenizer':
      {
        const {
          type,
          containerStyle,
          containerKey,
          ...rest
        } = item;
        return /*#__PURE__*/_jsx(Tokenizer, {
          ...rest,
          wrap: false
        });
      }
    case 'ToolbarSpacer':
      {
        const {
          type,
          style,
          ...rest
        } = item;
        return /*#__PURE__*/_jsx(ToolbarSpacer, {
          ...rest,
          style: {
            margin: '0',
            ...style
          }
        });
      }
    case 'MessageStrip':
      {
        const {
          messageStripProps
        } = item;
        return /*#__PURE__*/_jsx(MessageStrip, {
          ...messageStripProps
        });
      }
    default:
      {
        const {
          overflowPriority,
          menuItemText,
          responsiveSetId,
          ...rest
        } = item;
        return renderInnerToolbarItem(rest);
      }
  }
};
const getOverflowMenuButtonSections = (overflowedMenuItemIndices, overflowConfigs, items) => {
  const sections = [];
  let prevSectionEnded = true;
  const addMenuItems = (items, onSelectedHandlers, onItemClosedHandlers, separatedFromPrevSection) => {
    if (items.length === 0) return;
    const newSection = items.length > 1 || separatedFromPrevSection || prevSectionEnded;
    if (newSection) {
      sections.push({
        items: [...items],
        onSelectedHandlers: [...onSelectedHandlers],
        onItemClosedHandlers: [...onItemClosedHandlers]
      });
    } else {
      if (sections.length === 0) {
        // Initialize the first section
        sections.push({
          items: [],
          onSelectedHandlers: [],
          onItemClosedHandlers: []
        });
      }
      const lastSection = sections[sections.length - 1];
      // Append the menu item to the previous section
      lastSection.items.push(...items);
      lastSection.onSelectedHandlers.push(...onSelectedHandlers);
      lastSection.onItemClosedHandlers.push(...onItemClosedHandlers);
    }
    prevSectionEnded = items.length > 1;
  };
  for (const [index, overflowedMenuItemIndex] of overflowedMenuItemIndices.entries()) {
    const {
      menuItems,
      onSelected,
      onItemClosed
    } = overflowConfigs[overflowedMenuItemIndex] ?? {};
    if (!menuItems || menuItems.length === 0) continue;
    const separatedFromPrevSection = index > 0 && shouldBeSeparated(items, overflowedMenuItemIndices[index - 1], overflowedMenuItemIndex);

    // Single menu item is appended to previous section
    // Two or more menu items are appended as its own section
    // All the menu items in this section share one onSelected handler
    addMenuItems(menuItems, [onSelected], [onItemClosed], separatedFromPrevSection);
  }
  return sections;
};
const shouldBeSeparated = (items, startIndex, endIndex) => {
  if (endIndex > items.length) {
    // If the index is out of scope, early return
    // This would happen when items changes but the overflowed indices haven't been updated
    return false;
  }
  for (let i = startIndex + 1; i < endIndex; i++) {
    const type = items[i].type;
    if (type === 'ToolbarSeparator' || type === 'ToolbarSpacer') return true;
  }
  return false;
};
//# sourceMappingURL=BaseToolbar.js.map